/**
 * Custom PDF export service – converts TinyMCE document content to PDF
 * using html2canvas + jsPDF. Does not use TinyMCE's ExportPDF or external APIs.
 */

import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import timesFontContainer from 'pdfmake/build/standard-fonts/Times';
import htmlToPdfmake from 'html-to-pdfmake';
import { getRunningHead, resolveHeaderSlotText, normalizeHeaderSlot, HEADER_SLOT } from '../utils/pdfExportUtils';
import { applyTableFigureListsToExportHtml } from '../utils/tableFigureListUtils';

// pdfmake 0.3: register vfs via addVirtualFileSystem (assigning pdfMake.vfs is ignored).
const vfs = pdfFonts?.pdfMake?.vfs || pdfFonts?.vfs || pdfFonts;
if (vfs && typeof vfs === 'object') {
	if (typeof pdfMake.addVirtualFileSystem === 'function') {
		pdfMake.addVirtualFileSystem(vfs);
	} else {
		pdfMake.vfs = vfs;
	}
}

// Real Times (standard PDF fonts) — matches editor "Times New Roman" look for APA/thesis.
const timesVfs = timesFontContainer?.vfs || timesFontContainer;
const timesFaces = timesFontContainer?.fonts?.Times || {
	normal: 'Times-Roman',
	bold: 'Times-Bold',
	italics: 'Times-Italic',
	bolditalics: 'Times-BoldItalic',
};
if (timesVfs && typeof pdfMake.addVirtualFileSystem === 'function') {
	try {
		pdfMake.addVirtualFileSystem(timesVfs);
	} catch {
		/* already registered */
	}
}

const ROBOTO_FACE = {
	normal: 'Roboto-Regular.ttf',
	bold: 'Roboto-Medium.ttf',
	italics: 'Roboto-Italic.ttf',
	bolditalics: 'Roboto-MediumItalic.ttf',
};

pdfMake.fonts = {
	...(pdfMake.fonts || {}),
	Roboto: ROBOTO_FACE,
	// Prefer real Times for academic exports
	Times: timesFaces,
	TimesNewRoman: timesFaces,
	'Times New Roman': timesFaces,
	serif: timesFaces,
	Georgia: timesFaces,
	Arial: ROBOTO_FACE,
	Helvetica: ROBOTO_FACE,
	Calibri: ROBOTO_FACE,
};

const DEFAULT_PDF_FONT = 'Times';

// US Letter sizing and unit conversion (APA manuscripts)
const PAGE_WIDTH_MM = 215.9;
const PAGE_HEIGHT_MM = 279.4;
const MM_TO_PX = 3.779527559; // 96 DPI approximation
const SCALE = 1.5; // Higher scale for crisper output
const MARGIN_MM = 25.4; // 1 inch
const HEADER_SPACING_MM = 12.7; // 0.5 inch
const FOOTER_SPACING_MM = 12.7;
const HTML2CANVAS_TIMEOUT_MS = 60000;

const CONTENT_WIDTH_MM = PAGE_WIDTH_MM - 2 * MARGIN_MM;
const CONTENT_HEIGHT_MM =
  PAGE_HEIGHT_MM - 2 * MARGIN_MM - HEADER_SPACING_MM - FOOTER_SPACING_MM;
const PAGE_CONTENT_HEIGHT_CSS_PX = CONTENT_HEIGHT_MM * MM_TO_PX;
const PAGE_CONTENT_HEIGHT_PX = CONTENT_HEIGHT_MM * MM_TO_PX * SCALE;

const PDF_EXPORT_STYLE_ID = 'wf-pdf-export-typography';

/** Lowercase Roman numerals for dissertation front-matter page numbers (i–iii…). */
function toRomanLower(n) {
  if (n < 1 || n > 3999) return String(n);
  const vals = [1000, 900, 500, 400, 100, 90, 50, 40, 10, 9, 5, 4, 1];
  const syms = ['m', 'cm', 'd', 'cd', 'c', 'xc', 'l', 'xl', 'x', 'ix', 'v', 'iv', 'i'];
  let s = '';
  let num = n;
  for (let i = 0; i < vals.length; i += 1) {
    while (num >= vals[i]) {
      s += syms[i];
      num -= vals[i];
    }
  }
  return s;
}

/**
 * @param {number} pageIndex 0-based PDF page
 * @param {number} romanFrontPages if >0, first N pages use Roman; rest Arabic starting at 1
 */
function formatExportPageLabel(pageIndex, romanFrontPages = 0) {
  const physical = pageIndex + 1;
  if (romanFrontPages > 0 && physical <= romanFrontPages) {
    return toRomanLower(physical);
  }
  const arabic = physical - romanFrontPages;
  return String(Math.max(1, arabic));
}

/**
 * Mirrors key TinyMCE `content_style` rules so html2canvas matches the editor (indents, borderless tables, TOC leaders).
 */
const PDF_EXPORT_TYPOGRAPHY_CSS = `
.mce-content-body p { margin: 0; padding: 0; text-indent: 0.5in; }
.mce-content-body p.wf-no-indent,
.mce-content-body .mce-toc p,
.mce-content-body .wf-toc-tip,
.mce-content-body .wf-scaffold p,
.mce-content-body td p,
.mce-content-body th p,
.mce-content-body li p,
.mce-content-body blockquote p,
.mce-content-body h1 + p, .mce-content-body h2 + p, .mce-content-body h3 + p,
.mce-content-body h4 + p, .mce-content-body h5 + p, .mce-content-body h6 + p,
.mce-content-body div.page-break-before > p:first-of-type { text-indent: 0; }
.mce-content-body blockquote { margin: 0.5em 0 0.5em 0.5in !important; font-size: 12pt !important; line-height: 2 !important; border-left: none; padding-left: 0; }
.mce-content-body .wf-abstract-wrap p { text-indent: 0 !important; }
.mce-content-body table.wf-borderless:not(.wf-approval-table),
.mce-content-body table.wf-borderless:not(.wf-approval-table) td,
.mce-content-body table.wf-borderless:not(.wf-approval-table) th,
.mce-content-body table[border="0"]:not(.wf-approval-table),
.mce-content-body table[border="0"]:not(.wf-approval-table) td,
.mce-content-body table[border="0"]:not(.wf-approval-table) th {
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
}
.mce-content-body .wf-approval-signature {
  page-break-inside: avoid;
  break-inside: avoid;
}
.mce-content-body .wf-approval-signature .wf-sig-rule {
  display: block;
  border: 0;
  border-bottom: 1px solid #000;
  margin: 4px 0 0 0;
  height: 0;
  line-height: 0;
}
.mce-content-body .wf-approval-signature .wf-sig-fill {
  display: inline-block;
  min-width: 200px;
  border-bottom: 1px solid #000;
  line-height: 1.4;
  min-height: 1.2em;
  vertical-align: bottom;
  text-decoration: none !important;
}
.mce-content-body .wf-approval-signature .wf-sig-date-fill { min-width: 110px; }
.mce-content-body h2.wf-level1-heading { text-align: center !important; }
.mce-content-body p.wf-keywords-line { text-indent: 0.5in !important; margin-top: 0; }
.mce-content-body .mce-toc li.wf-toc-branch {
  list-style: none !important;
}
.mce-content-body .mce-toc .wf-toc-entry {
  display: flex;
  align-items: baseline;
  flex-wrap: nowrap;
  list-style: none;
  margin: 0;
  padding: 0;
  line-height: 2;
}
.mce-content-body .mce-toc .wf-toc-title {
  flex: 0 1 auto;
  text-decoration: none;
  color: #000;
  font-size: 12pt;
}
.mce-content-body .mce-toc .wf-toc-leader {
  flex: 1 1 auto;
  border-bottom: 1px dotted #666;
  min-width: 1.5rem;
  margin: 0 0.35rem;
  height: 0.55em;
  align-self: flex-end;
  margin-bottom: 0.4rem;
}
.mce-content-body .mce-toc .wf-toc-page {
  flex: 0 0 auto;
  font-size: 12pt;
  font-variant-numeric: tabular-nums;
  color: #333;
}
.mce-content-body .mce-toc ul ul .wf-toc-title { font-size: 11pt; }
.mce-content-body .wf-generated-list-block h2 { margin: 0 0 0.5em 0; }
.mce-content-body .wf-generated-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.mce-content-body .wf-generated-list-item {
  display: flex;
  align-items: baseline;
  gap: 0.35rem;
  line-height: 2;
}
.mce-content-body .wf-generated-list-title {
  text-decoration: none;
  color: #000;
}
.mce-content-body .wf-generated-list-leader {
  flex: 1 1 auto;
  border-bottom: 1px dotted #666;
  min-width: 1.5rem;
  height: 0.55em;
  margin-bottom: 0.4rem;
}
.mce-content-body .wf-generated-list-page {
  font-variant-numeric: tabular-nums;
}
.mce-content-body .wf-generated-list-empty {
  list-style: none;
  color: #666;
  font-style: italic;
}
.mce-content-body table.wf-imported-table {
  width: 100% !important;
  border-collapse: collapse !important;
  margin: 1em 0 1.25em 0 !important;
  font-size: 11pt !important;
  line-height: 1.4 !important;
}
.mce-content-body table.wf-imported-table th,
.mce-content-body table.wf-imported-table td {
  border: 1px solid #333 !important;
  padding: 6px 8px !important;
  text-align: left !important;
  vertical-align: top !important;
  text-indent: 0 !important;
}
.mce-content-body table.wf-imported-table th {
  font-weight: bold !important;
  background: #f5f5f5 !important;
}
.mce-content-body table.wf-imported-table p {
  text-indent: 0 !important;
  margin: 0 !important;
}
.mce-content-body ul, .mce-content-body ol {
  margin: 0.15em 0 0.35em 0.5in;
  padding-left: 0.22in;
}
.mce-content-body h1, .mce-content-body h2, .mce-content-body h3,
.mce-content-body h4, .mce-content-body h5, .mce-content-body h6 {
  margin: 0;
  padding: 0;
  font-size: 12pt;
  line-height: 2;
}
.mce-content-body h2.wf-references-heading {
  text-align: center !important;
  font-weight: bold !important;
  margin-bottom: 1em !important;
}
.mce-content-body p.reference-entry {
  text-indent: -0.5in !important;
  padding-left: 0.5in !important;
  margin: 0 0 0.35em 0 !important;
}
.mce-content-body .wf-scaffold,
.mce-content-body .wf-toc-tip { display: none !important; }
.mce-content-body .page-break-before {
  border-top: none !important;
  margin-top: 0 !important;
  padding-top: 0 !important;
}
.mce-content-body .mce-pagebreak {
  display: none !important;
  height: 0 !important;
  margin: 0 !important;
  border: none !important;
}
`;

function injectPdfExportTypography(container) {
  if (!container.querySelector(`#${PDF_EXPORT_STYLE_ID}`)) {
    const style = document.createElement('style');
    style.id = PDF_EXPORT_STYLE_ID;
    style.textContent = PDF_EXPORT_TYPOGRAPHY_CSS;
    container.insertBefore(style, container.firstChild);
  }
}

/**
 * Create a temporary container with TinyMCE-style content for rendering
 */
function createContentContainer(htmlContent, widthPx) {
  const container = document.createElement('div');
  container.className = 'mce-content-body';
  container.innerHTML = htmlContent || '<p></p>';
  injectPdfExportTypography(container);
  container.style.cssText = `
    width: ${widthPx}px;
    min-height: auto;
    height: auto;
    overflow: visible;
    position: absolute;
    left: -99999px;
    top: 0;
    background: #fff;
    padding: 0;
    box-sizing: border-box;
    font-family: "Times New Roman", Times, serif;
    font-size: 12pt;
    line-height: 2;
    color: #000;
  `;
  document.body.appendChild(container);
  return container;
}

/**
 * Populate TOC page numbers from actual anchor positions in export DOM.
 * This happens before rasterization, so rendered TOC shows real page refs.
 */
function applyTocPageReferences(container, romanFrontPages = 0) {
  const tocEntries = Array.from(container.querySelectorAll('.mce-toc .wf-toc-entry'));
  if (tocEntries.length === 0) return;

  const containerTop = container.getBoundingClientRect().top;
  tocEntries.forEach((entry) => {
    const link = entry.querySelector('.wf-toc-title[href^="#"]');
    const pageEl = entry.querySelector('.wf-toc-page');
    if (!link || !pageEl) return;

    const targetId = (link.getAttribute('href') || '').slice(1);
    if (!targetId) return;
    const target = container.querySelector(`#${CSS.escape(targetId)}`);
    if (!target) return;

    const targetTop = target.getBoundingClientRect().top - containerTop;
    const pageIndex = Math.max(0, Math.floor(targetTop / Math.max(1, PAGE_CONTENT_HEIGHT_CSS_PX)));
    pageEl.textContent = formatExportPageLabel(pageIndex, romanFrontPages);
  });
}

/**
 * Wait until all images in the export container are loaded/decoded.
 * This prevents html2canvas from snapshotting before chart images (data URLs) are ready.
 */
async function waitForContainerImages(container, timeoutMs = 8000) {
  const images = Array.from(container.querySelectorAll('img'));
  if (images.length === 0) return;

  const waitOne = (img) =>
    new Promise((resolve) => {
      if (img.complete && img.naturalWidth > 0) return resolve();

      const done = () => {
        img.removeEventListener('load', done);
        img.removeEventListener('error', done);
        resolve();
      };

      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    });

  // Prefer decode when available for better paint readiness.
  const decodePromises = images.map((img) => {
    if (typeof img.decode === 'function') {
      return img.decode().catch(() => waitOne(img));
    }
    return waitOne(img);
  });

  await Promise.race([
    Promise.all(decodePromises),
    new Promise((resolve) => setTimeout(resolve, timeoutMs))
  ]);
}

/**
 * Render HTML content to one or more page canvases, honoring section page breaks.
 * html2canvas cannot paginate by CSS page-break, so we split the DOM first.
 */
async function renderHtmlChunksToPageCanvases(htmlContent, widthPx, romanFrontPages = 0) {
  // Prepare typography/lists, but keep section break classes for chunking.
  // (preprocessHtmlForPdfMake strips classes for pdfmake — do that only for text path.)
  const prepared = prepareHtmlForPdfMake(htmlContent || '');

  const doc = new DOMParser().parseFromString(`<div id="wf-export-root">${prepared}</div>`, 'text/html');
  const root = doc.getElementById('wf-export-root');
  if (!root) {
    const canvas = await renderContentToCanvas(prepared, widthPx, romanFrontPages);
    return [canvas];
  }

  const chunks = [];
  let current = doc.createElement('div');

  const flush = () => {
    const html = current.innerHTML.trim();
    if (html) chunks.push(html);
    current = doc.createElement('div');
  };

  const hasPageBreakBefore = (el) => {
    if (!el || el.nodeType !== 1) return false;
    if (el.classList?.contains('page-break-before')) return true;
    if (el.classList?.contains('wf-pdf-page-break') || el.classList?.contains('mce-pagebreak')) return true;
    if (el.getAttribute?.('data-wf-page-break') === 'before') return true;
    const style = (el.getAttribute('style') || '').toLowerCase();
    return /page-break-before\s*:\s*always/.test(style) || /break-before\s*:\s*page/.test(style);
  };

  Array.from(root.childNodes).forEach((node) => {
    if (node.nodeType === 1 && hasPageBreakBefore(node) && (node.classList?.contains('wf-pdf-page-break') || node.classList?.contains('mce-pagebreak') || node.getAttribute?.('data-wf-page-break') === 'before') && !(node.textContent || '').trim()) {
      flush();
      return;
    }
    if (node.nodeType === 1 && hasPageBreakBefore(node)) {
      flush();
      const clone = node.cloneNode(true);
      clone.classList.remove('page-break-before');
      clone.querySelectorAll('.wf-pdf-page-break, .mce-pagebreak').forEach((m) => m.remove());
      // Clear break styles so html2canvas doesn't leave a blank top gap
      if (clone.getAttribute?.('style')) {
        clone.setAttribute(
          'style',
          clone
            .getAttribute('style')
            .replace(/page-break-before\s*:\s*always;?/gi, '')
            .replace(/break-before\s*:\s*page;?/gi, '')
        );
      }
      current.appendChild(clone);
      return;
    }
    current.appendChild(node.cloneNode(true));
  });
  flush();

  if (chunks.length === 0) chunks.push('<p></p>');

  const pageCanvases = [];
  for (let i = 0; i < chunks.length; i += 1) {
    const canvas = await renderContentToCanvas(chunks[i], widthPx, romanFrontPages);
    // Paginate oversized chunks so text isn't sliced mid-line as often
    let offset = 0;
    while (offset < canvas.height && pageCanvases.length < 500) {
      const remaining = canvas.height - offset;
      if (remaining < PAGE_CONTENT_HEIGHT_PX * 0.02) break;
      const sliceHeight = Math.min(PAGE_CONTENT_HEIGHT_PX, remaining);
      const pageCanvas = document.createElement('canvas');
      pageCanvas.width = canvas.width;
      pageCanvas.height = Math.max(1, Math.floor(sliceHeight));
      const ctx = pageCanvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
      ctx.drawImage(
        canvas,
        0,
        Math.floor(offset),
        canvas.width,
        sliceHeight,
        0,
        0,
        pageCanvas.width,
        pageCanvas.height
      );
      pageCanvases.push(pageCanvas);
      offset += sliceHeight;
    }
  }

  return pageCanvases.length ? pageCanvases : [await renderContentToCanvas('<p></p>', widthPx, romanFrontPages)];
}

/**
 * Render HTML content to canvas
 */
async function renderContentToCanvas(htmlContent, widthPx, romanFrontPages = 0) {
  const container = createContentContainer(htmlContent, widthPx);

  try {
    await new Promise((resolve) => setTimeout(resolve, 50));
    await waitForContainerImages(container);
    applyTocPageReferences(container, romanFrontPages);

    const canvas = await new Promise((resolve, reject) => {
      const timer = setTimeout(
        () => reject(new Error('PDF render timeout')),
        HTML2CANVAS_TIMEOUT_MS
      );
      html2canvas(container, {
        scale: SCALE,
        useCORS: true,
        allowTaint: true,
        imageTimeout: 5000,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: container.scrollWidth,
      })
        .then((c) => {
          clearTimeout(timer);
          resolve(c);
        })
        .catch((err) => {
          clearTimeout(timer);
          reject(err);
        });
    });

    return canvas;
  } finally {
    if (container.parentNode) {
      container.parentNode.removeChild(container);
    }
  }
}

/**
 * Remove editor-only scaffolding (e.g. INTRODUCTION/METHOD nav labels) before rasterizing HTML.
 * @param {string} html
 * @returns {string}
 */
export function stripExportOmitFromHtml(html) {
  if (!html || typeof html !== 'string') return html;
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div id="wf-export-root">${html}</div>`, 'text/html');
    const root = doc.getElementById('wf-export-root');
    if (!root) return html;
    root.querySelectorAll('.wf-export-omit, .wf-scaffold, .wf-toc-tip').forEach((el) => el.remove());

    // Drop empty paragraphs left after scaffold removal
    root.querySelectorAll('p').forEach((p) => {
      const text = (p.textContent || '').replace(/\u00a0/g, ' ').trim();
      if (!text && !p.querySelector('img,table,hr,canvas,svg')) p.remove();
    });

    return root.innerHTML;
  } catch {
    return html;
  }
}

function shouldUseRasterPdf(html) {
  if (!html || typeof html !== 'string') return false;
  return /<canvas\b|wf-chart|chart\.js|data-wf-chart/i.test(html);
}

const MAJOR_SECTION_HEADING =
	/^(abstract|table of contents|list of tables|list of figures|acknowledgments?|acknowledgements?|dedication|references|appendices|appendix(\s+[a-z0-9]+)?|chapter\s+\d+)\b/i;

function preprocessHtmlForPdfMake(html) {
	if (!html || typeof html !== 'string') return html;
	try {
		const doc = new DOMParser().parseFromString(`<div id="wf-export-root">${html}</div>`, 'text/html');
		const root = doc.getElementById('wf-export-root');
		if (!root) return html;

		// html-to-pdfmake crashes on customTag empty markers — apply CSS page-break on real content instead.
		root.querySelectorAll('.page-break-before').forEach((el) => {
			el.classList.remove('page-break-before');
			const first =
				el.querySelector('h1,h2,h3,h4,h5,h6,p,table,ul,ol,div.wf-title-page,div.wf-abstract-wrap') ||
				el.firstElementChild ||
				el;
			mergeInlineStyle(first, 'page-break-before: always');
		});

		root.querySelectorAll('.mce-pagebreak, .wf-pdf-page-break, [data-wf-page-break="before"]').forEach((el) => {
			const next =
				el.nextElementSibling ||
				el.parentElement?.querySelector('h1,h2,h3,h4,h5,h6,p,table,ul,ol');
			if (next) mergeInlineStyle(next, 'page-break-before: always');
			el.remove();
		});

		// Belt-and-suspenders: major front-matter / chapter headings always start a page
		// even if the editor omitted .page-break-before on the wrapper.
		let seenContent = false;
		root.querySelectorAll('h1,h2,p').forEach((el) => {
			const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
			if (!text) return;
			const looksLikeListEntry = /\.{2,}|…/.test(text) || /\s+\d{1,3}$/.test(text);
			const isMajor =
				!looksLikeListEntry && text.length < 70 && MAJOR_SECTION_HEADING.test(text);

			if (isMajor) {
				if (seenContent) mergeInlineStyle(el, 'page-break-before: always');
				seenContent = true;
				return;
			}
			seenContent = true;
		});

		return root.innerHTML;
	} catch {
		return html;
	}
}

function mergeInlineStyle(el, cssText) {
	if (!el || el.nodeType !== 1) return;
	const existing = (el.getAttribute('style') || '').trim().replace(/;?\s*$/, '');
	const next = cssText.trim().replace(/;?\s*$/, '');
	// Avoid duplicating the same declaration
	if (existing.toLowerCase().includes(next.toLowerCase())) return;
	el.setAttribute('style', existing ? `${existing}; ${next}` : next);
}

/** Map editor/CSS font names onto registered pdfmake faces. */
function resolvePdfMakeFont(fontName) {
	if (!fontName || typeof fontName !== 'string') return undefined;
	const raw = fontName.split(',')[0].replace(/['"]/g, '').trim();
	if (!raw) return undefined;
	const registered = pdfMake.fonts || {};
	if (registered[raw]) return raw;
	const compact = raw.replace(/\s+/g, '');
	if (registered[compact]) return compact;
	const lower = raw.toLowerCase();
	if (lower.includes('times') || lower.includes('georgia') || lower.includes('serif')) {
		return DEFAULT_PDF_FONT;
	}
	if (lower.includes('arial') || lower.includes('helvetica') || lower.includes('calibri') || lower.includes('sans') || lower.includes('roboto')) {
		return registered.Roboto ? 'Roboto' : DEFAULT_PDF_FONT;
	}
	return DEFAULT_PDF_FONT;
}

function nodePlainText(node) {
	if (!node) return '';
	if (typeof node.text === 'string') return node.text;
	if (Array.isArray(node.text)) {
		return node.text.map((t) => (typeof t === 'string' ? t : nodePlainText(t))).join('');
	}
	return '';
}

function applyPageBreakBefore(node) {
	if (!node || typeof node !== 'object') return;
	node.pageBreak = 'before';
	delete node.pageBreakBefore;
}

/**
 * html-to-pdfmake emits pageBreakBefore; pdfmake expects pageBreak: 'before'.
 * Also normalize fonts/spacing and force thesis major-section page breaks.
 */
function normalizePdfMakeContent(node, ctx = { seenContent: false, sectionIndex: 0 }) {
	if (Array.isArray(node)) {
		node.forEach((child) => normalizePdfMakeContent(child, ctx));
		return;
	}
	if (!node || typeof node !== 'object') return;

	if (node.pageBreakBefore === 'always' || node.pageBreakBefore === true) {
		applyPageBreakBefore(node);
	}

	// Always use Times for body academic look unless an explicit sans face was chosen.
	if (node.font) {
		node.font = resolvePdfMakeFont(node.font) || DEFAULT_PDF_FONT;
	}

	// APA-ish double spacing; keep tables tighter
	const isTableCell = node.nodeName === 'TD' || node.nodeName === 'TH';
	if (node.lineHeight == null && (node.text != null || node.stack || node.ul || node.ol)) {
		node.lineHeight = isTableCell ? 1.3 : 2;
	} else if (typeof node.lineHeight === 'number' && node.lineHeight > 0 && node.lineHeight < 1.2 && !isTableCell) {
		node.lineHeight = 2;
	}

	// Keep headings at 12pt (html-to-pdfmake defaults h1 to 24)
	if (/^H[1-6]$/i.test(node.nodeName || '') && (!node.fontSize || node.fontSize > 12)) {
		node.fontSize = 12;
	}

	const plain = nodePlainText(node).replace(/\s+/g, ' ').trim();

	// Zero default paragraph margins, but keep intentional vertical spacers
	if (node.nodeName === 'P' && Array.isArray(node.margin)) {
		const [, top = 0, , bottom = 0] = node.margin;
		const keepSpacer = top >= VSPACER_PT - 2 || bottom >= VSPACER_PT - 2;
		if (!keepSpacer) node.margin = [0, 0, 0, 0];
	}
	if (/^H[1-6]$/i.test(node.nodeName || '')) {
		node.margin = [0, 0, 0, 0];
		node.marginBottom = 0;
	}

	// Hide transparent spacer glyphs
	if (plain === '.' && Array.isArray(node.margin) && (node.margin[3] >= VSPACER_PT - 2 || node.margin[1] >= VSPACER_PT - 2)) {
		node.color = 'white';
		node.fontSize = 1;
		node.lineHeight = 1;
	}

	// Force a new page before major thesis headings when CSS break was lost.
	// Skip TOC/LoT/LoF entries (leader dots or trailing page numbers).
	const isHeadingNode = /^H[1-6]$/i.test(node.nodeName || '');
	const looksLikeListEntry = /\.{2,}|…/.test(plain) || /\s+\d{1,3}$/.test(plain);
	const isMajorHeading =
		plain &&
		plain !== '.' &&
		!looksLikeListEntry &&
		MAJOR_SECTION_HEADING.test(plain) &&
		plain.length < 70 &&
		(isHeadingNode || node.bold === true || plain.split(/\s+/).length <= 6);

	if (isMajorHeading) {
		if (ctx.seenContent) applyPageBreakBefore(node);
		ctx.seenContent = true;
	} else if (plain && plain !== '.') {
		ctx.seenContent = true;
	}

	// Only page-break section wrappers that open with a major heading — never Keywords-only pages
	if (
		node.nodeName === 'DIV' &&
		node.id &&
		/^section-\d+/i.test(node.id) &&
		Array.isArray(node.stack) &&
		node.stack.length
	) {
		const sectionText = node.stack
			.map((child) => nodePlainText(child))
			.join(' ')
			.replace(/\s+/g, ' ')
			.trim();
		const isKeywordsOnly =
			/^keywords\s*:/i.test(sectionText) &&
			sectionText.length < 220 &&
			!/abstract|table of contents|list of|acknowledgment|chapter|reference|appendix/i.test(sectionText);
		const opensWithMajor = node.stack.some((child) => {
			const t = nodePlainText(child).replace(/\s+/g, ' ').trim();
			return t && t !== '.' && !/\.{2,}|…/.test(t) && MAJOR_SECTION_HEADING.test(t) && t.length < 70;
		});
		if (!isKeywordsOnly && opensWithMajor && ctx.sectionIndex > 0) {
			applyPageBreakBefore(node.stack[0]);
		}
		if (!isKeywordsOnly) ctx.sectionIndex += 1;
	}

	if (node.stack) normalizePdfMakeContent(node.stack, ctx);
	if (Array.isArray(node.ul)) normalizePdfMakeContent(node.ul, ctx);
	if (Array.isArray(node.ol)) normalizePdfMakeContent(node.ol, ctx);
	if (node.table?.body) {
		node.table.body.forEach((row) => {
			if (Array.isArray(row)) normalizePdfMakeContent(row, ctx);
		});
	}
	if (Array.isArray(node.text)) normalizePdfMakeContent(node.text, ctx);
	if (Array.isArray(node.columns)) normalizePdfMakeContent(node.columns, ctx);
}

function isVisuallyEmptyParagraph(el) {
	const text = (el.textContent || '').replace(/\u00a0/g, ' ').trim();
	if (text && text !== '.') return false;
	if (el.querySelector('img,table,hr,canvas,svg')) return false;
	return true;
}

/** One double-spaced 12pt line ≈ 24pt — recreates editor <p><br></p> vertical rhythm. */
const VSPACER_PT = 22;

/**
 * Title/abstract pages use empty <p><br></p> rows for vertical centering in the editor.
 * Convert those into real bottom-margin spacers before we strip "empty" paragraphs.
 */
function convertEmptyParagraphsToSpacers(root, doc) {
	root.querySelectorAll('p').forEach((p) => {
		if (p.getAttribute('data-wf-vspacer')) return;
		if (!isVisuallyEmptyParagraph(p)) return;
		const spacer = doc.createElement('p');
		spacer.className = 'wf-vspacer wf-no-indent';
		spacer.setAttribute('data-wf-vspacer', '1');
		spacer.setAttribute(
			'style',
			`margin:0 0 ${VSPACER_PT}pt 0; padding:0; font-size:1pt; line-height:1; color:transparent; text-indent:0;`
		);
		spacer.textContent = '.';
		p.replaceWith(spacer);
	});
}

function sectionPlainText(el) {
	return (el?.textContent || '').replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
}

function isKeywordsOnlySection(el) {
	const text = sectionPlainText(el);
	if (!text) return false;
	if (/abstract|table of contents|list of|acknowledgment|dedication|chapter|reference|appendix/i.test(text)) {
		return false;
	}
	return /^keywords\s*:/i.test(text) && text.length < 220;
}

/**
 * Inline TinyMCE/editor styles so html-to-pdfmake (which ignores most classes)
 * produces spacing/tables/lists that match the editor.
 */
function prepareHtmlForPdfMake(html) {
	if (!html || typeof html !== 'string') return html || '';
	try {
		const doc = new DOMParser().parseFromString(`<div id="wf-export-root">${html}</div>`, 'text/html');
		const root = doc.getElementById('wf-export-root');
		if (!root) return html;

		// Drop editor-only scaffold / tips (empty TOC placeholders, etc.)
		root.querySelectorAll('.wf-scaffold, .wf-toc-tip, .wf-export-omit').forEach((el) => el.remove());

		// Honors template has a separate optional Keywords section AND keywords under Abstract.
		// That Keywords-only section became the orphan one-line page — drop the duplicate.
		const abstractHasKeywords = /abstract[\s\S]{0,2000}keywords\s*:/i.test(root.textContent || '');
		root.querySelectorAll('div[id^="section-"]').forEach((section) => {
			if (!isKeywordsOnlySection(section)) return;
			if (abstractHasKeywords) {
				section.remove();
				return;
			}
			section.classList.remove('page-break-before');
		});

		// Keep vertical rhythm from empty paragraphs (title page centering, etc.)
		convertEmptyParagraphsToSpacers(root, doc);

		// Abstract pages in the editor often sit lower on the page; add top spacers when missing.
		root.querySelectorAll('div[id^="section-"]').forEach((section) => {
			const heading = section.querySelector('h1,h2');
			const headingText = (heading?.textContent || '').replace(/\s+/g, ' ').trim();
			const isAbstract = /^abstract$/i.test(headingText);
			const isTitle =
				/^section-0$/i.test(section.id) ||
				/thesis title/i.test(sectionPlainText(section).slice(0, 80));
			if (!isAbstract && !isTitle) return;

			let leadingSpacers = 0;
			for (const child of Array.from(section.children)) {
				if (child.getAttribute?.('data-wf-vspacer')) leadingSpacers += 1;
				else if (isVisuallyEmptyParagraph(child)) leadingSpacers += 1;
				else break;
			}
			const want = isTitle ? 5 : 6;
			for (let i = leadingSpacers; i < want; i += 1) {
				const spacer = doc.createElement('p');
				spacer.className = 'wf-vspacer wf-no-indent';
				spacer.setAttribute('data-wf-vspacer', '1');
				spacer.setAttribute(
					'style',
					`margin:0 0 ${VSPACER_PT}pt 0; padding:0; font-size:1pt; line-height:1; color:transparent; text-indent:0;`
				);
				spacer.textContent = '.';
				section.insertBefore(spacer, section.firstChild);
			}
		});

		// Remove any remaining truly empty paragraphs (no spacer mark)
		root.querySelectorAll('p').forEach((p) => {
			if (p.getAttribute('data-wf-vspacer')) return;
			if (isVisuallyEmptyParagraph(p)) p.remove();
		});

		// Collapse leftover empty wrappers (except section anchors)
		root.querySelectorAll('div').forEach((div) => {
			if (div.classList.contains('wf-pdf-page-break') || div.classList.contains('mce-pagebreak')) return;
			if (div.id && (div.id.startsWith('section-') || div.id.startsWith('subsection-'))) return;
			if (div.classList.contains('page-break-before')) return;
			const text = (div.textContent || '').replace(/\u00a0/g, ' ').trim();
			if (!text && !div.querySelector('img,table,hr,canvas,svg,ul,ol')) {
				// Keep if it only holds a page-break marker
				if (div.querySelector('.wf-pdf-page-break, .mce-pagebreak')) return;
				div.remove();
			}
		});

		// Fix smashed List of Tables / Figures (template or Word import stored entries in one <p>)
		const splitSmashedListParagraph = (p) => {
			const text = (p.textContent || '').replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
			if (!/^(Table|Figure)\s*\d+/i.test(text)) return;
			const parts = text
				.split(/(?=(?:Table|Figure)\s*\d+)/i)
				.map((s) => s.trim())
				.filter(Boolean);
			if (parts.length < 2) return;
			const frag = doc.createDocumentFragment();
			parts.forEach((part) => {
				const np = doc.createElement('p');
				np.className = 'wf-no-indent';
				np.setAttribute('style', 'margin:0; text-indent:0; line-height:2');
				np.textContent = part;
				frag.appendChild(np);
			});
			p.replaceWith(frag);
		};

		root.querySelectorAll('p').forEach(splitSmashedListParagraph);

		// Also catch smashed entries inside a single block that starts with a heading + entries
		root.querySelectorAll('div, section').forEach((block) => {
			const directPs = Array.from(block.children).filter((c) => c.tagName === 'P');
			directPs.forEach(splitSmashedListParagraph);
		});

		root.querySelectorAll('h1,h2,h3,h4,h5,h6').forEach((h) => {
			const tag = h.tagName.toLowerCase();
			const parts = [
				'margin:0',
				'padding:0',
				'font-size:12pt',
				'line-height:2',
				'font-family:"Times New Roman",Times,serif'
			];
			if (tag === 'h1' || h.classList.contains('wf-level1-heading') || h.classList.contains('wf-references-heading')) {
				parts.push('text-align:center', 'font-weight:bold');
			} else if (tag === 'h2') {
				parts.push('text-align:left', 'font-weight:bold');
			} else if (tag === 'h3') {
				parts.push('text-align:left', 'font-weight:bold', 'font-style:italic');
			} else {
				parts.push('font-weight:bold');
			}
			if (h.classList.contains('ql-align-center')) parts.push('text-align:center');
			mergeInlineStyle(h, parts.join('; '));
		});

		root.querySelectorAll('p').forEach((p) => {
			if (p.getAttribute('data-wf-vspacer')) return; // keep spacer margins

			const noIndent =
				p.classList.contains('wf-no-indent') ||
				p.classList.contains('ql-align-center') ||
				p.closest('td,th,li,blockquote,.wf-abstract-wrap,.wf-title-page,.mce-toc') ||
				p.classList.contains('reference-entry') ||
				p.classList.contains('wf-keywords-line');

			const parts = [
				'margin:0',
				'padding:0',
				'font-size:12pt',
				'line-height:2',
				'font-family:"Times New Roman",Times,serif',
				`text-indent:${noIndent ? '0' : '0.5in'}`
			];

			if (p.classList.contains('ql-align-center') || p.closest('.wf-title-page')) {
				parts.push('text-align:center');
			}
			if (p.classList.contains('reference-entry')) {
				parts.push('text-indent:-0.5in', 'padding-left:0.5in', 'margin:0 0 6pt 0');
			}
			mergeInlineStyle(p, parts.join('; '));
		});

		root.querySelectorAll('ul,ol').forEach((list) => {
			mergeInlineStyle(
				list,
				'margin:4pt 0 8pt 36pt; padding-left:12pt; line-height:2; font-size:12pt'
			);
		});

		root.querySelectorAll('li').forEach((li) => {
			mergeInlineStyle(li, 'margin:0; padding:0; line-height:2');
		});

		root.querySelectorAll('table.wf-imported-table, table').forEach((table) => {
			const isImported = table.classList.contains('wf-imported-table');
			const isBorderless =
				table.classList.contains('wf-borderless') ||
				table.getAttribute('border') === '0' ||
				table.classList.contains('wf-approval-table');

			if (isImported) {
				mergeInlineStyle(
					table,
					'width:100%; border-collapse:collapse; margin:12pt 0; font-size:11pt; line-height:1.4'
				);
				table.querySelectorAll('th,td').forEach((cell) => {
					const isTh = cell.tagName.toLowerCase() === 'th';
					mergeInlineStyle(
						cell,
						`border:1px solid #333; padding:6px 8px; text-align:left; vertical-align:top; text-indent:0;${
							isTh ? ' font-weight:bold; background-color:#f5f5f5;' : ''
						}`
					);
				});
				table.querySelectorAll('p').forEach((p) => {
					mergeInlineStyle(p, 'margin:0; padding:0; text-indent:0; line-height:1.4; font-size:11pt');
				});
			} else if (isBorderless) {
				mergeInlineStyle(table, 'border:none; border-collapse:collapse; margin:0');
				table.querySelectorAll('th,td').forEach((cell) => {
					mergeInlineStyle(cell, 'border:none; padding:2px 0; text-indent:0');
				});
			}
		});

		return root.innerHTML;
	} catch {
		return html;
	}
}

/**
 * Build a pdfmake cell for one header/footer slot (left or right).
 * Supports none | pageNumber | runningHead | documentTitle on either side.
 */
function buildPdfSlotCell(slot, alignment, { runningHead, documentTitle, pageLabel }) {
	const normalized = normalizeHeaderSlot(slot);
	if (normalized === HEADER_SLOT.NONE) {
		return { text: '', alignment };
	}
	if (normalized === HEADER_SLOT.PAGE_NUMBER) {
		return { text: pageLabel, alignment };
	}
	if (normalized === HEADER_SLOT.RUNNING_HEAD) {
		return {
			text: resolveHeaderSlotText(normalized, { runningHead, documentTitle }),
			alignment,
			style: 'runningHead',
		};
	}
	if (normalized === HEADER_SLOT.DOCUMENT_TITLE) {
		return {
			text: resolveHeaderSlotText(normalized, { runningHead, documentTitle }),
			alignment,
		};
	}
	return { text: '', alignment };
}

function hasAnyHeaderFooterSlot(config) {
	return (
		normalizeHeaderSlot(config?.left) !== HEADER_SLOT.NONE ||
		normalizeHeaderSlot(config?.right) !== HEADER_SLOT.NONE
	);
}

function buildPdfHeaderFooter(runningHead, documentTitle, header, footer, romanFront) {
	const texts = { runningHead, documentTitle };

	const headerFn = hasAnyHeaderFooterSlot(header)
		? (currentPage) => {
				const pageLabel = formatExportPageLabel(currentPage - 1, romanFront);
				return {
					columns: [
						buildPdfSlotCell(header.left, 'left', { ...texts, pageLabel }),
						buildPdfSlotCell(header.right, 'right', { ...texts, pageLabel }),
					],
					margin: [72, 28, 72, 0],
					fontSize: 12,
				};
			}
		: undefined;

	const footerFn = hasAnyHeaderFooterSlot(footer)
		? (currentPage) => {
				const pageLabel = formatExportPageLabel(currentPage - 1, romanFront);
				return {
					columns: [
						buildPdfSlotCell(footer.left, 'left', { ...texts, pageLabel }),
						buildPdfSlotCell(footer.right, 'right', { ...texts, pageLabel }),
					],
					margin: [72, 0, 72, 28],
					fontSize: 12,
				};
			}
		: undefined;

	return { header: headerFn, footer: footerFn };
}

/** Draw one slot for raster (jsPDF) export. */
function drawRasterSlot(pdf, slot, y, align, pdfWidth, { runningHead, documentTitle, pageLabel }) {
	const normalized = normalizeHeaderSlot(slot);
	if (normalized === HEADER_SLOT.NONE) return;

	const x = align === 'right' ? pdfWidth - MARGIN_MM : MARGIN_MM;
	const opts = align === 'right' ? { align: 'right' } : undefined;

	if (normalized === HEADER_SLOT.PAGE_NUMBER) {
		pdf.setFont('helvetica', 'normal');
		pdf.setFontSize(11);
		pdf.text(pageLabel, x, y, opts);
		return;
	}

	const text = resolveHeaderSlotText(normalized, { runningHead, documentTitle });
	if (!text) return;

	if (normalized === HEADER_SLOT.RUNNING_HEAD) {
		pdf.setFont('helvetica', 'bold');
	} else {
		pdf.setFont('helvetica', 'normal');
	}
	pdf.setFontSize(11);
	pdf.text(text, x, y, opts);
	pdf.setFont('helvetica', 'normal');
}

/**
 * Text-based PDF export (selectable/searchable copy) via pdfmake + html-to-pdfmake.
 */
async function exportDocumentToPdfText({
  contentHtml,
  title = 'document',
  config = null,
  hasWatermark = false,
  romanNumeralFrontPages = 0,
}) {
  const runningHead = getRunningHead(title);
  const header = config?.header || { left: HEADER_SLOT.NONE, right: HEADER_SLOT.PAGE_NUMBER };
  const footer = config?.footer || { left: HEADER_SLOT.NONE, right: HEADER_SLOT.NONE };
  const romanFront =
    Number.isFinite(Number(romanNumeralFrontPages)) && Number(romanNumeralFrontPages) > 0
      ? Math.min(500, Math.floor(Number(romanNumeralFrontPages)))
      : 0;

  const cleanedHtml = stripExportOmitFromHtml(contentHtml || '');
  const enrichedHtml = applyTableFigureListsToExportHtml(cleanedHtml);
  const styledHtml = prepareHtmlForPdfMake(enrichedHtml);
  const pdfReadyHtml = preprocessHtmlForPdfMake(styledHtml);

  let pdfContent;
  try {
    pdfContent = htmlToPdfmake(
      `<div class="mce-content-body">${pdfReadyHtml}</div>`,
      {
        window,
        tableAutoSize: true,
        removeExtraBlanks: true,
        defaultStyles: {
          p: { margin: [0, 0, 0, 0] },
          h1: { fontSize: 12, bold: true, alignment: 'center', margin: [0, 0, 0, 0] },
          h2: { fontSize: 12, bold: true, margin: [0, 0, 0, 0] },
          h3: { fontSize: 12, bold: true, italics: true, margin: [0, 0, 0, 0] },
          h4: { fontSize: 12, bold: true, margin: [0, 0, 0, 0] },
          h5: { fontSize: 12, bold: true, margin: [0, 0, 0, 0] },
          h6: { fontSize: 12, bold: true, margin: [0, 0, 0, 0] },
          ul: { margin: [0, 2, 0, 18] },
          ol: { margin: [0, 2, 0, 18] },
          li: { margin: [0, 0, 0, 0] },
          table: { margin: [0, 8, 0, 10] },
          th: { bold: true, fillColor: '#f5f5f5' },
        },
      }
    );
  } catch (err) {
    console.error('html-to-pdfmake failed', err);
    throw err;
  }

  normalizePdfMakeContent(pdfContent);

  const { header: headerFn, footer: footerFn } = buildPdfHeaderFooter(
    runningHead,
    title,
    header,
    footer,
    romanFront
  );

  const docDefinition = {
    content: Array.isArray(pdfContent) ? pdfContent : [pdfContent],
    pageSize: 'LETTER',
    pageMargins: [72, 72, 72, 72],
    defaultStyle: {
      font: DEFAULT_PDF_FONT,
      fontSize: 12,
      lineHeight: 2,
    },
    styles: {
      runningHead: { bold: true },
      'wf-level1-heading': { alignment: 'center', bold: true },
      'wf-references-heading': { alignment: 'center', bold: true },
      'ql-align-center': { alignment: 'center' },
      'wf-no-indent': {},
      'reference-entry': {},
    },
    header: headerFn,
    footer: footerFn,
    background: hasWatermark
      ? (currentPage, pageSize) => ({
          text: 'WriteForm',
          color: '#cccccc',
          opacity: 0.35,
          bold: true,
          fontSize: 72,
          alignment: 'center',
          margin: [0, pageSize.height / 2 - 36, 0, 0],
        })
      : undefined,
  };

  const fileName = `${title || 'document'}.pdf`;

  // pdfmake 0.3 getBlob/getBuffer are Promise-based (callbacks are ignored).
  const pdf = pdfMake.createPdf(docDefinition);
  const blob = await pdf.getBlob();
  return {
    blob,
    save: async (name = fileName) => {
      if (typeof pdf.download === 'function') {
        await pdf.download(name || fileName);
        return;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name || fileName;
      a.click();
      URL.revokeObjectURL(url);
    },
  };
}

/**
 * Image-based PDF export (fallback for charts/complex layouts).
 */
async function exportDocumentToPdfRaster({
  contentHtml,
  title = 'document',
  config = null,
  hasWatermark = false,
  romanNumeralFrontPages = 0,
}) {
  const runningHead = getRunningHead(title);
  const header = config?.header || { left: HEADER_SLOT.NONE, right: HEADER_SLOT.PAGE_NUMBER };
  const footer = config?.footer || { left: HEADER_SLOT.NONE, right: HEADER_SLOT.NONE };
  const romanFront =
    Number.isFinite(Number(romanNumeralFrontPages)) && Number(romanNumeralFrontPages) > 0
      ? Math.min(500, Math.floor(Number(romanNumeralFrontPages)))
      : 0;

  const widthPx = Math.round(CONTENT_WIDTH_MM * MM_TO_PX * SCALE);

  const cleanedHtml = stripExportOmitFromHtml(contentHtml || '');
  const enrichedHtml = applyTableFigureListsToExportHtml(cleanedHtml);
  const pageCanvases = await renderHtmlChunksToPageCanvases(enrichedHtml, widthPx, romanFront);

  const pageImages = pageCanvases.map((c) => c.toDataURL('image/png'));

  if (pageImages.length === 0) {
    pageImages.push(document.createElement('canvas').toDataURL('image/png'));
  }

  const pdf = new jsPDF({ unit: 'mm', format: 'letter' });
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();

  pageImages.forEach((img, idx) => {
    if (idx) pdf.addPage();

    // Watermark first so it sits behind the content
    if (hasWatermark) {
      pdf.setTextColor(200, 200, 200);
      pdf.setFontSize(64);
      pdf.setFont('helvetica', 'bold');
      pdf.text('WriteForm', pdfWidth / 2, pdfHeight / 2, {
        align: 'center',
        angle: -45,
      });
    }

    pdf.setTextColor(0, 0, 0);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(11);

    const imgProps = pdf.getImageProperties(img);
    const aspect = imgProps.height / imgProps.width;
    let renderWidthMm = CONTENT_WIDTH_MM;
    let renderHeightMm = renderWidthMm * aspect;
    if (renderHeightMm > CONTENT_HEIGHT_MM) {
      const s = CONTENT_HEIGHT_MM / renderHeightMm;
      renderWidthMm *= s;
      renderHeightMm = CONTENT_HEIGHT_MM;
    }
    const x = MARGIN_MM;
    const y = MARGIN_MM + HEADER_SPACING_MM;
    pdf.addImage(img, 'PNG', x, y, renderWidthMm, renderHeightMm);

    // Header — honor left/right slots independently (running head, title, page #)
    const headerY = MARGIN_MM - 2;
    const pageLabel = formatExportPageLabel(idx, romanFront);
    const slotTexts = { runningHead, documentTitle: title || '' };
    drawRasterSlot(pdf, header.left, headerY, 'left', pdfWidth, {
      ...slotTexts,
      pageLabel,
    });
    drawRasterSlot(pdf, header.right, headerY, 'right', pdfWidth, {
      ...slotTexts,
      pageLabel,
    });

    // Footer
    const footerY = pdfHeight - MARGIN_MM + 4;
    drawRasterSlot(pdf, footer.left, footerY, 'left', pdfWidth, {
      ...slotTexts,
      pageLabel,
    });
    drawRasterSlot(pdf, footer.right, footerY, 'right', pdfWidth, {
      ...slotTexts,
      pageLabel,
    });
  });

  const blob = pdf.output('blob');
  const fileName = `${title || 'document'}.pdf`;

  return {
    blob,
    save: (name = fileName) => pdf.save(name || fileName),
  };
}

/**
 * Export document to PDF — text-based by default (selectable copy); raster fallback for charts.
 *
 * @param {object} opts
 * @param {string} opts.contentHtml - HTML from TinyMCE editor
 * @param {string} opts.title - Document title (for filename and running head)
 * @param {string} opts.documentType - Document type name (e.g. 'Academic Paper')
 * @param {object} opts.config - { header: { left, right }, footer: { left, right } }
 * @param {boolean} opts.hasWatermark - Whether to add WriteForm watermark
 * @returns {Promise<{ blob: Blob, save: (name?: string) => void }>}
 */
export async function exportDocumentToPdf(opts) {
  if (!shouldUseRasterPdf(opts.contentHtml)) {
    try {
      return await exportDocumentToPdfText(opts);
    } catch (err) {
      console.warn('Text PDF export failed; falling back to raster export.', err);
    }
  }
  return exportDocumentToPdfRaster(opts);
}
